import type {
  RegulatoryQuestionnaire,
  RegulatoryPresentation,
  EquipmentConfiguration,
  EquipmentConfigurationWrite,
  EquipmentContextQuestionnaire,
  CatalogFilters,
  CatalogHistory,
  CatalogPage,
  CatalogRevision,
  CatalogTemplate,
  CatalogWrite,
  ModelPack,
  PointRevision,
  ReferenceEdition,
  ResolvedCatalog,
  SourceDocument,
  TemplateDetail,
} from "../types/regulatory"

export class RegulatoryApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message)
    this.name = "RegulatoryApiError"
  }
}
const cache = new Map<string, { etag: string; value: unknown }>()
let sessionKey: string | null = null
let cacheGeneration = 0
export function regulatorySessionGeneration() {
  return cacheGeneration
}
export function resetRegulatoryCache() {
  cache.clear()
  cacheGeneration++
}
export function setRegulatorySession(key: string | null) {
  if (key !== sessionKey) {
    sessionKey = key
    resetRegulatoryCache()
  }
}
export function parseApiError(text: string, status: number): RegulatoryApiError {
  let message = text || `Erreur HTTP ${status}`
  let code = "request_failed"
  try {
    const body = JSON.parse(text)
    if (typeof body?.error?.message === "string") message = body.error.message
    else if (typeof body?.Reason === "string") message = body.Reason
    if (typeof body?.error?.code === "string") code = body.error.code
  } catch {
    /* Non-JSON responses retain their message. */
  }
  return new RegulatoryApiError(status, code, message)
}
export function catalogQuery(filters: CatalogFilters = {}) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters))
    if (value !== undefined && value !== "") params.set(key, String(value))
  const query = params.toString()
  return query ? `?${query}` : ""
}
async function request<T>(
  path: string,
  signal?: AbortSignal,
  method = "GET",
  body?: unknown,
): Promise<T> {
  const generation = cacheGeneration
  const activeSession = sessionKey
  const operational = /(?:questionnaire|configuration)(?:[/?]|$)/.test(path)
  const cached = method === "GET" && !operational ? cache.get(path) : undefined
  const headers: Record<string, string> = { Accept: "application/json" }
  if (cached) headers["If-None-Match"] = cached.etag
  if (body !== undefined) headers["Content-Type"] = "application/json"
  const response = await fetch(`/api/v2/regulatory/${path}`, {
    method,
    cache: operational ? "no-store" : "default",
    credentials: "same-origin",
    headers,
    signal,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (activeSession !== sessionKey && method === "GET")
    throw new DOMException("La session ou le catalogue a changé", "AbortError")
  if (response.status === 304 && cached) return structuredClone(cached.value) as T
  if (!response.ok) {
    if (response.status === 401) {
      resetRegulatoryCache()
      if (typeof window !== "undefined") window.dispatchEvent(new Event("cloud-session-expired"))
    }
    throw parseApiError(await response.text(), response.status)
  }
  let value: T
  try {
    value = (await response.json()) as T
  } catch {
    throw new RegulatoryApiError(
      response.status,
      "invalid_response",
      "L’API a renvoyé une réponse JSON invalide. Vérifiez la disponibilité de la version v2.",
    )
  }
  if (method !== "GET") resetRegulatoryCache()
  else {
    const etag = response.headers.get("ETag")
    if (
      !operational &&
      !response.headers.get("Cache-Control")?.includes("no-store") &&
      etag &&
      generation === cacheGeneration
    )
      cache.set(path, { etag, value: structuredClone(value) })
  }
  return value
}
const id = encodeURIComponent
export const regulatoryApi = {
  equipmentConfiguration: (uuid: string, signal?: AbortSignal) =>
    request<EquipmentConfiguration>(`equipment/${id(uuid)}/configuration`, signal),
  equipmentQuestionnaire: (uuid: string, signal?: AbortSignal) =>
    request<EquipmentContextQuestionnaire>(
      `equipment/${id(uuid)}/questionnaire?format=contexts`,
      signal,
    ),
  previewEquipmentConfiguration: (uuid: string, body: EquipmentConfigurationWrite) =>
    request<EquipmentContextQuestionnaire>(
      `equipment/${id(uuid)}/configuration/preview`,
      undefined,
      "POST",
      body,
    ),
  publishEquipmentConfiguration: (uuid: string, body: EquipmentConfigurationWrite) =>
    request<EquipmentContextQuestionnaire>(
      `equipment/${id(uuid)}/configuration`,
      undefined,
      "PUT",
      body,
    ),
  presentation: (template: string, revision: string, signal?: AbortSignal) =>
    request<RegulatoryPresentation>(
      `templates/${id(template)}/revisions/${id(revision)}/presentation`,
      signal,
    ),
  publishPresentation: (
    template: string,
    revision: string,
    body: {
      expectedProfileUuid: string | null
      reviewed: boolean
      assignments: Record<string, string>
    },
  ) =>
    request<RegulatoryPresentation>(
      `templates/${id(template)}/revisions/${id(revision)}/presentation`,
      undefined,
      "POST",
      body,
    ),
  questionnaire: (type: "site" | "equipment", uuid: string, signal?: AbortSignal) =>
    request<RegulatoryQuestionnaire>(
      `${type === "site" ? "sites" : "equipment"}/${id(uuid)}/questionnaire`,
      signal,
    ),
  templates: (filters?: CatalogFilters, signal?: AbortSignal) =>
    request<CatalogPage<CatalogTemplate>>(`templates${catalogQuery(filters)}`, signal),
  template: (uuid: string, signal?: AbortSignal) =>
    request<TemplateDetail>(`templates/${id(uuid)}`, signal),
  revisions: (uuid: string, filters?: CatalogFilters, signal?: AbortSignal) =>
    request<CatalogPage<CatalogRevision>>(
      `templates/${id(uuid)}/revisions${catalogQuery(filters)}`,
      signal,
    ),
  revision: (uuid: string, revisionUuid: string, signal?: AbortSignal) =>
    request<CatalogRevision>(`templates/${id(uuid)}/revisions/${id(revisionUuid)}`, signal),
  resolved: (uuid: string, revisionUuid: string, signal?: AbortSignal) =>
    request<ResolvedCatalog>(
      `templates/${id(uuid)}/revisions/${id(revisionUuid)}/resolved`,
      signal,
    ),
  history: (uuid: string, filters?: CatalogFilters, signal?: AbortSignal) =>
    request<CatalogPage<CatalogHistory>>(
      `templates/${id(uuid)}/history${catalogQuery(filters)}`,
      signal,
    ),
  points: (filters?: CatalogFilters, signal?: AbortSignal) =>
    request<CatalogPage<PointRevision>>(`point-revisions${catalogQuery(filters)}`, signal),
  point: (uuid: string, signal?: AbortSignal) =>
    request<PointRevision>(`point-revisions/${id(uuid)}`, signal),
  references: (filters?: CatalogFilters, signal?: AbortSignal) =>
    request<CatalogPage<ReferenceEdition>>(`reference-editions${catalogQuery(filters)}`, signal),
  documents: (filters?: CatalogFilters, signal?: AbortSignal) =>
    request<CatalogPage<SourceDocument>>(`source-documents${catalogQuery(filters)}`, signal),
  packs: (filters?: CatalogFilters, signal?: AbortSignal) =>
    request<CatalogPage<ModelPack>>(`model-packs${catalogQuery(filters)}`, signal),
  create: (body: CatalogWrite) => request<CatalogRevision>("templates", undefined, "POST", body),
  revise: (uuid: string, body: CatalogWrite) =>
    request<CatalogRevision>(`templates/${id(uuid)}/revisions`, undefined, "POST", body),
  clone: (uuid: string, name: string, revisionUuid?: string) =>
    request<CatalogRevision>(`templates/${id(uuid)}/clone`, undefined, "POST", {
      name,
      revisionUuid,
    }),
  archive: (uuid: string, archived: boolean, expectedCurrentRevisionUuid: string) =>
    request<CatalogTemplate>(`templates/${id(uuid)}`, undefined, "PATCH", {
      archived,
      expectedCurrentRevisionUuid,
    }),
}
// For selectors with no search endpoint, explicitly load every page before local search.
export async function allCatalogPages<T>(
  load: (filters: CatalogFilters, signal?: AbortSignal) => Promise<CatalogPage<T>>,
  signal?: AbortSignal,
) {
  const values: T[] = []
  for (let offset = 0; ; offset += 200) {
    const page = await load({ limit: 200, offset }, signal)
    values.push(...page.items)
    if (values.length >= page.total || page.items.length === 0) return values
  }
}
